"use client";

import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { Home, ChevronRight, ArrowUpRight, MapPin } from "lucide-react";
import AeropuertosNav from "./components/AeropuertosNav";
import { AIRPORTS } from "./airports";

const NAVY = "#102542";
const YELLOW = "#ffb800";

const HERO_IMAGE =
  "https://galeria.tocumenaero.com/gallery/api/Aeropuerto Scarlett Martínez Río Hato/R__o_Hato_1.jpg";

export default function AeropuertosPage() {
  const t = useTranslations("aeropuertosPage");
  const tCrumb = useTranslations("breadcrumb");
  const locale = useLocale();

  const main = AIRPORTS.find((a) => a.isMain)!;
  const regional = AIRPORTS.filter((a) => !a.isMain);

  return (
    <>
      <AeropuertosNav />

      <main className="bg-white">
        {/* Hero */}
        <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
          <Image
            src={HERO_IMAGE}
            alt={t("hero.title")}
            fill
            priority
            sizes="100vw"
            className="object-cover blur-xs"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10" />

          <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex items-center gap-2 text-xs text-white/80 flex-wrap">
                <li>
                  <Link
                    href={`/${locale}`}
                    className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
                  >
                    <Home className="w-3.5 h-3.5" />
                    <span>{tCrumb("home")}</span>
                  </Link>
                </li>
                <li aria-hidden>
                  <ChevronRight className="w-3.5 h-3.5 text-white/40" />
                </li>
                <li
                  aria-current="page"
                  className="text-white font-medium truncate"
                >
                  {t("breadcrumb")}
                </li>
              </ol>
            </nav>

            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
                <span
                  className="w-8 h-px"
                  style={{ backgroundColor: YELLOW }}
                />
                {t("hero.eyebrow")}
              </span>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight text-balance">
                {t("hero.title")}
              </h1>
              <p className="text-white/80 text-base md:text-lg leading-relaxed">
                {t("hero.subtitle")}
              </p>
            </div>
          </div>
        </section>

        {/* Aeropuerto principal — featured */}
        <section className="bg-white">
          <div className="max-w-[80%] mx-auto py-24">
            <div className="max-w-3xl mb-14">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("main.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                {t("main.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                {t("main.subtitle")}
              </p>
            </div>

            <Link
              href={`/${locale}${main.href}`}
              className="group relative bg-white grid grid-cols-1 lg:grid-cols-2 items-stretch transition-colors hover:bg-gray-50 overflow-hidden rounded-2xl border border-gray-200"
            >
              <span
                className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 z-10"
                style={{ backgroundColor: YELLOW }}
              />
              <div className="relative w-full h-72 lg:h-auto min-h-[320px] overflow-hidden bg-gray-100">
                <Image
                  src={main.image}
                  alt={t(`airports.${main.id}.name`)}
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-10 md:p-12 flex flex-col justify-center">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-400">
                      {t("codeLabel")}
                    </span>
                    <span
                      className="text-2xl font-bold tabular-nums tracking-tight"
                      style={{ color: NAVY }}
                    >
                      {main.code}
                    </span>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-gray-400 transition-all duration-300 group-hover:text-[#102542] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>

                <h3
                  className="text-2xl md:text-3xl font-bold tracking-tight mb-4"
                  style={{ color: NAVY }}
                >
                  {t(`airports.${main.id}.name`)}
                </h3>
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-5">
                  <MapPin className="w-4 h-4" />
                  <span>
                    {t(`airports.${main.id}.city`)},{" "}
                    {t(`airports.${main.id}.province`)}
                  </span>
                </div>
                <p className="text-base text-gray-600 leading-relaxed mb-8">
                  {t(`airports.${main.id}.description`)}
                </p>

                <span
                  className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold transition-all group-hover:gap-3"
                  style={{ color: NAVY }}
                >
                  {t("exploreButton")}
                  <span
                    className="w-6 h-px transition-all duration-300 group-hover:w-12"
                    style={{ backgroundColor: YELLOW }}
                  />
                </span>
              </div>
            </Link>
          </div>
        </section>

        {/* Aeropuertos regionales */}
        <section className="bg-gray-50 border-y border-gray-200">
          <div className="max-w-[80%] mx-auto py-24">
            <div className="max-w-3xl mb-14">
              <span
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold mb-4"
                style={{ color: NAVY }}
              >
                <span className="w-8 h-px" style={{ backgroundColor: NAVY }} />
                {t("regional.eyebrow")}
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                {t("regional.title")}
              </h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                {t("regional.subtitle")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
              {regional.map((airport, idx) => (
                <Link
                  key={airport.id}
                  href={`/${locale}${airport.href}`}
                  className="group relative bg-white flex flex-col transition-colors hover:bg-gray-50 overflow-hidden"
                >
                  <span
                    className="absolute top-0 left-0 right-0 h-0.5 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 z-10"
                    style={{ backgroundColor: YELLOW }}
                  />

                  <div className="relative w-full aspect-[4/3] overflow-hidden bg-gray-100">
                    <Image
                      src={airport.image}
                      alt={t(`airports.${airport.id}.name`)}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>

                  <div className="p-8 flex flex-col flex-1">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-mono uppercase tracking-wider text-gray-400 tabular-nums">
                          {String(idx + 1).padStart(2, "0")}
                        </span>
                        <span className="text-gray-300">·</span>
                        <span
                          className="text-[11px] font-mono uppercase tracking-wider tabular-nums"
                          style={{ color: NAVY }}
                        >
                          {airport.code}
                        </span>
                      </div>
                      <ArrowUpRight className="w-4 h-4 text-gray-400 transition-all duration-300 group-hover:text-[#102542] group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </div>

                    <h3
                      className="text-lg font-bold tracking-tight mb-2"
                      style={{ color: NAVY }}
                    >
                      {t(`airports.${airport.id}.name`)}
                    </h3>
                    <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-4">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>
                        {t(`airports.${airport.id}.city`)},{" "}
                        {t(`airports.${airport.id}.province`)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed mb-6 flex-1">
                      {t(`airports.${airport.id}.description`)}
                    </p>

                    <span
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider font-semibold transition-all group-hover:gap-2.5"
                      style={{ color: NAVY }}
                    >
                      {t("exploreButton")}
                      <span
                        className="w-6 h-px transition-all duration-300 group-hover:w-10"
                        style={{ backgroundColor: YELLOW }}
                      />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}
